import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router";
import { db } from "../../firebase/client";
import { resetBroadcast } from "../../redux/broadcast/broadcast.actions";
import { selectBroadcastId } from "../../redux/broadcast/broadcast.selectors";

interface Stats {
	totalViewers: number;
	viewerBuckets: Record<string, number>;
	reactionTotals: Record<string, number>;
	topPost: { text: string; date: string; reactionCount: number } | null;
	computedAt: string;
}

interface BroadcastData {
	homeTeam: string;
	awayTeam: string;
	homeScore: number;
	awayScore: number;
	userId: string;
	stats?: Stats;
}

const BroadcastStats = () => {
	const { id } = useParams<{ id: string }>();
	const [broadcast, setBroadcast] = useState<BroadcastData | null>(null);
	const [loading, setLoading] = useState(true);
	const dispatch = useDispatch();
	const trackedBroadcastId = useSelector(selectBroadcastId);

	useEffect(() => {
		if (!id) return;
		return onSnapshot(doc(db, "broadcasts", id), (snap) => {
			setBroadcast(snap.exists() ? (snap.data() as BroadcastData) : null);
			setLoading(false);
		});
	}, [id]);

	// Landing here for the broadcast the author was actively tracking means
	// they were redirected after it ended — safe to drop that redux slice
	// (and its localStorage mirror) now that this page no longer needs it.
	useEffect(() => {
		if (id && trackedBroadcastId === id) dispatch(resetBroadcast());
	}, [id, trackedBroadcastId, dispatch]);

	if (loading) return <div>Loading…</div>;
	if (!broadcast) return <div>Broadcast not found.</div>;

	const stats = broadcast.stats;
	const { homeTeam, awayTeam, homeScore, awayScore, userId } = broadcast;

	return (
		<div>
			<p><Link to={`/author/${userId}`}>← Back to profile</Link></p>
			<h2>{homeTeam} {homeScore} – {awayScore} {awayTeam}</h2>
			<h3>Broadcast stats</h3>

			{!stats ? (
				<p>Stats haven't been computed for this broadcast yet.</p>
			) : (
				<>
					<section>
						<h4>Viewers</h4>
						<p><strong>{stats.totalViewers}</strong> unique viewer{stats.totalViewers !== 1 ? "s" : ""}</p>
						{Object.keys(stats.viewerBuckets).length > 0 && (
							<table>
								<tbody>
									{Object.entries(stats.viewerBuckets).map(([label, count]) => (
										<tr key={label}>
											<td>{label}</td>
											<td><strong>{count}</strong></td>
											<td>
												{stats.totalViewers > 0
													? `${Math.round((count / stats.totalViewers) * 100)}%`
													: "—"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</section>

					<section>
						<h4>Reactions</h4>
						{Object.keys(stats.reactionTotals).length === 0 ? (
							<p>No reactions recorded.</p>
						) : (
							<ul>
								{Object.entries(stats.reactionTotals)
									.sort((a, b) => b[1] - a[1])
									.map(([emoji, count]) => (
										<li key={emoji}>{emoji} — {count}</li>
									))}
							</ul>
						)}
					</section>

					{stats.topPost && (
						<section>
							<h4>Most reacted post</h4>
							<blockquote>"{stats.topPost.text}"</blockquote>
							<p>{stats.topPost.reactionCount} reaction{stats.topPost.reactionCount !== 1 ? "s" : ""}</p>
						</section>
					)}

					<p><small>Computed {new Date(stats.computedAt).toLocaleString()}</small></p>
				</>
			)}
		</div>
	);
};

export default BroadcastStats;
